var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/*
SPDX-FileCopyrightText: 2023 Genome Research Ltd.

SPDX-License-Identifier: MIT
*/
import { WidthProvider, Responsive } from 'react-grid-layout';
import { Button, Placeholder } from '../index';
import { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { getWidgetOrder } from './Utils';
import { resetAllFilters, removeComponent } from '../filtering/Utils';
var ResponsiveReactGridLayout = WidthProvider(Responsive);
function ResponsiveWidget(props) {
    var widgets = props.widgets, draggable = props.draggable, setOrder = props.setOrder, setWidgets = props.setWidgets, zone = props.zone;
    var _a = useState(generateLayout(widgets)), layoutsState = _a[0], setLayouts = _a[1];
    var internalLayouts = useRef(generateLayout(widgets));
    useEffect(function () {
        var newLayout = generateLayout(widgets);
        setLayouts(newLayout);
        internalLayouts.current = newLayout;
    }, [widgets]);
    var deleteWidget = function (id) {
        var newComponents = Object.keys(widgets.components)
            .filter(function (key) { return key !== id; })
            .reduce(function (obj, key) {
            obj[key] = widgets.components[key];
            return obj;
        }, {});
        var newOrder = widgets.order.filter(function (key) { return key !== id; });
        setWidgets({ components: newComponents, order: newOrder });
        // Going to have to make it remove it from the zone and then set the zone again to fire off the useEffect in the ZoneGrid
        removeComponent(id, zone);
        resetAllFilters(zone);
    };
    function onLayoutChange(layout) {
        //saveToLS("layouts", layouts, id);
        var order = getWidgetOrder(layout, widgets);
        if (setOrder) {
            setOrder(order);
        }
        zone.order = order.order;
        var newLayout = generateLayout(order);
        if (JSON.stringify(newLayout) !== JSON.stringify(layoutsState)) {
            internalLayouts.current = newLayout;
        }
    }
    var onBreakpointChange = function () {
        if (JSON.stringify(internalLayouts.current) !== JSON.stringify(layoutsState)) {
            setLayouts(internalLayouts.current);
        }
    };
    function generateLayout(components) {
        var types = {
            small: { lg: { w: 1, h: 1 }, md: { w: 1, h: 1 }, sm: { w: 1, h: 1 } },
            medium: { lg: { w: 2, h: 2 }, md: { w: 2, h: 2 }, sm: { w: 1, h: 2 } },
            large: { lg: { w: 4, h: 2 }, md: { w: 2, h: 2 }, sm: { w: 1, h: 2 } }
        };
        var layout = { lg: [], md: [], sm: [] };
        var y = { lg: 0, md: 0, sm: 0 };
        var x = { lg: 0, md: 0, sm: 0 };
        components.order.forEach(function (id) {
            var widget = components.components[id];
            if (!widget) {
                return;
            }
            ['lg', 'md', 'sm'].forEach(function (breakpoint) {
                var _a = types[widget.size][breakpoint], w = _a.w, h = _a.h;
                // if the widget won't fit on the current row, move it to the next row
                if (x[breakpoint] + w > (breakpoint === 'lg' ? 4 : breakpoint === 'md' ? 2 : 1)) {
                    y[breakpoint] += h;
                    x[breakpoint] = 0;
                }
                layout[breakpoint].push({ i: id, x: x[breakpoint], y: y[breakpoint], w: w, h: h });
                x[breakpoint] += w;
            });
        });
        return layout;
    }
    return (_jsx("div", __assign({ className: 'tol-responsive-grid' }, { children: _jsx(ResponsiveReactGridLayout, __assign({ layouts: layoutsState, breakpoints: { lg: 992, md: 576, sm: 0 }, cols: { lg: 4, md: 2, sm: 1 }, isDraggable: draggable, compactType: 'vertical', rowHeight: 150, onLayoutChange: onLayoutChange, onBreakpointChange: onBreakpointChange, draggableCancel: '.widget-delete-btn' }, { children: widgets.order.map(function (key) {
                // Check if there is a component that matches the ids
                if (!widgets.components[key]) {
                    return null;
                }
                if (!draggable) {
                    return (_jsx("div", __assign({ className: 'tol-responsive-widget' }, { children: widgets.components[key].element }), key));
                }
                else {
                    return (_jsxs("div", __assign({ className: 'tol-draggable-widget' }, { children: [_jsx(Placeholder, { opacity: 0.7, drag: true, message: key }), _jsx(Button, __assign({ onClick: function () {
                                    deleteWidget(key);
                                }, variant: 'danger', className: 'widget-delete-btn' }, { children: _jsx(FontAwesomeIcon, { icon: faTrash, size: 'sm' }) }))] }), key));
                }
            }) })) })));
}
export default ResponsiveWidget;
